import { createStore } from 'vuex'
import { getProductList } from '@/api/product'
import { processProductImageUrlSync } from '@/utils/imageUtils'
import { isWechatBrowser } from '@/utils/wechatEnvironment'

// Import image assets directly
import shopImage from '@/assets/shop.jpg'
import shop1Image from '@/assets/shop1.jpg'

export default createStore({
  state: {
    products: [],
    // 用户认证状态
    user: null,
    token: null,
    isLoggedIn: false,
    // 微信登录相关状态
    wechatLoginState: {
      isProcessing: false,
      error: null
    }
  },
  mutations: {
    setProducts(state, products) {
      state.products = products
    },
    appendProducts(state, products) {
      state.products = [...state.products, ...products]
    },
    // 用户认证相关 mutations
    setUser(state, userData) {
      state.user = userData
      state.isLoggedIn = true
    },
    setToken(state, token) {
      state.token = token
    },
    clearUser(state) {
      state.user = null
      state.token = null
      state.isLoggedIn = false
    },
    initUserFromStorage(state) {
      try {
        if (typeof localStorage === 'undefined') {
          return
        }
        
        // 优先检查标准token
        let token = localStorage.getItem('token')
        let userInfo = localStorage.getItem('userInfo')
        
        // 如果没有标准token，检查微信token
        if (!token) {
          token = localStorage.getItem('wechat_token')
          userInfo = localStorage.getItem('wechat_user_info')
        }
        
        if (token && typeof token === 'string') {
          state.token = token
          state.isLoggedIn = true
        }
        
        if (userInfo && typeof userInfo === 'string') {
          const parsedUser = JSON.parse(userInfo)
          if (parsedUser && typeof parsedUser === 'object') {
            state.user = parsedUser
          }
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error)
        // Clear potentially corrupted data
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('wechat_token')
          localStorage.removeItem('wechat_user_info')
        }
        state.token = null
        state.user = null
        state.isLoggedIn = false
      }
    },
    // 微信登录状态管理
    setWechatLoginProcessing(state, isProcessing) {
      state.wechatLoginState.isProcessing = isProcessing
    },
    setWechatLoginError(state, error) {
      state.wechatLoginState.error = error
    },
    clearWechatLoginError(state) {
      state.wechatLoginState.error = null
    }
  },
  actions: {
    // 设置用户信息
    setUser({ commit }, userData) {
      commit('setUser', userData)
    },
    
    // 设置token
    setToken({ commit }, token) {
      commit('setToken', token)
    },
    
    // 用户登录
    login({ commit }, { user, token }) {
      if (!user || !token) {
        console.error('Login failed: user or token is null or undefined')
        return
      }
      
      try {
        commit('setUser', user)
        commit('setToken', token)
        // 保存到本地存储
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem('token', token)
          localStorage.setItem('userInfo', JSON.stringify(user))
        }
      } catch (error) {
        console.error('Login error:', error)
      }
    },
    
    // 用户退出
    logout({ commit }) {
      try {
        commit('clearUser')
        // 清除本地存储
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('wechat_token')
          localStorage.removeItem('wechat_user_info')
        }
      } catch (error) {
        console.error('Logout error:', error)
      }
    },
    
    // 微信登录处理
    async wechatLogin({ commit }) {
      commit('setWechatLoginProcessing', true)
      commit('clearWechatLoginError')
      
      try {
        // 这里应该调用实际的API
        // const response = await api.wechatLogin({ code, state })
        
        // 模拟API调用
        const response = await new Promise((resolve) => {
          setTimeout(() => {
            resolve({
              success: true,
              data: {
                user: {
                  id: '123456',
                  nickname: '微信用户',
                  avatar: 'https://via.placeholder.com/100x100',
                  openid: 'mock_openid_' + Date.now()
                },
                token: 'mock_token_' + Date.now()
              }
            })
          }, 1000)
        })
        
        if (response.success) {
          commit('setUser', response.data.user)
          commit('setToken', response.data.token)
          
          // 保存到本地存储
          localStorage.setItem('token', response.data.token)
          localStorage.setItem('userInfo', JSON.stringify(response.data.user))
          
          return response.data
        } else {
          throw new Error(response.message || '登录失败')
        }
      } catch (error) {
        commit('setWechatLoginError', error.message)
        throw error
      } finally {
        commit('setWechatLoginProcessing', false)
      }
    },
    
    // 初始化用户状态
    initUser({ commit }) {
      try {
        if (typeof localStorage === 'undefined') {
          console.warn('localStorage is not available')
          return
        }
        commit('initUserFromStorage')
      } catch (error) {
        console.error('Error initializing user:', error)
      }
    },
    
    async fetchProducts({ commit }, { typeIds = null, page = 1, limit = 10, append = false } = {}) {
      try {
        let apiResponse
        if (typeIds && typeIds.length > 0) {
          // 根据分类ID获取商品
          apiResponse = await getProductList(typeIds.toString(), page, limit)
        } else {
          // 获取所有分类的商品（全部）
          apiResponse = await getProductList('1,2,3,4,5,6,7,8,9,10,11', page, limit)
        }
        
        // 处理新的API响应数据格式
        const responseData = apiResponse.data || apiResponse
        const apiProducts = responseData.data?.list || responseData.list || responseData
        
        // 将API数据转换为前端需要的格式
        const products = Array.isArray(apiProducts) ? apiProducts.map((item, index) => {
          // 处理图片URL，确保使用HTTP协议
          let imageUrl = item.image || item.images
          
          // 如果API返回的图片URL存在，确保使用HTTP协议
          if (imageUrl) {
            imageUrl = processProductImageUrlSync(imageUrl)
          } else {
            // 如果没有图片，使用本地备用图片
            imageUrl = index % 2 === 0 ? shopImage : shop1Image
          }
          
          /**
           * 根据产品类型获取产品信息
           * @param {number} productType - 产品类型：1=现货, 2=定制, 3=预售
           * @param {number} customDuration - 定制时长（小时）
           * @returns {Object} 包含标签和发货时间的对象
           */
          const getProductTypeInfo = (productType, customDuration) => {
            /**
             * 格式化发货时间：≤72小时显示小时，>72小时显示天
             * @param {number} duration - 时长（小时）
             * @returns {string} 格式化后的发货时间字符串
             */
            const formatDeliveryTime = (duration) => {
              if (duration <= 72) {
                return `${duration}小时内发货`
              } else {
                const days = Math.ceil(duration / 24)
                return `${days}天内发货`
              }
            }
            
            switch (productType) {
              case 1:
                return { tag: '现货', deliveryTime: '24小时内发货' }
              case 2:
                return { tag: '定制', deliveryTime: formatDeliveryTime(customDuration || 720) }
              case 3:
                return { tag: '预售', deliveryTime: formatDeliveryTime(customDuration || 168) }
              default:
                return { tag: '现货', deliveryTime: '24小时内发货' }
            }
          }
          
          const typeInfo = getProductTypeInfo(item.product_type, item.custom_duration)
          
          return {
            id: item.id,
            name: `【${typeInfo.tag}】${item.name}`, // 在标题前添加类型标签
            price: item.price, // 直接使用API返回的价格
            image: imageUrl,
            deliveryTime: typeInfo.deliveryTime,
            productType: item.product_type, // 原始产品类型
            productTypeTag: typeInfo.tag, // 用于显示的标签
            description: item.description,
            stock: item.stock,
            status: item.status,
            // 保持使用 type_id 以匹配 API 响应
            type_id: item.type_id,
            salesCount: item.sales_count || 0,
            viewCount: item.view_count || 0,
            customDuration: item.custom_duration || 0
          }
        }) : []
        
        if (append) {
          commit('appendProducts', products)
        } else {
          commit('setProducts', products)
        }
        
        // 返回分页信息和产品数据
        return {
          products,
          pagination: {
            page: responseData.data?.page || page,
            limit: responseData.data?.limit || limit,
            total: responseData.data?.total || products.length,
            pages: responseData.data?.pages || Math.ceil((responseData.data?.total || products.length) / limit)
          }
        }
      } catch (error) {
        console.error('获取商品列表失败:', error)
        // 如果API调用失败，使用备用数据
        const fallbackProducts = [
          {
            id: 1,
            name: '【定制】2023 蒙古丽人传统服装',
            price: 6500.00,
            image: shopImage,
            deliveryTime: '30天',
            productType: 2,
            productTypeTag: '定制'
          },
          {
            id: 2,
            name: '【定制】蒙古丽人AB款传统服装',
            price: 10000.00,
            image: shop1Image,
            deliveryTime: '30天',
            productType: 2,
            productTypeTag: '定制'
          }
        ]
        commit('setProducts', fallbackProducts)
      }
    }
  },
  getters: {
    isWechat: () => {
      return isWechatBrowser()
    },
    // 用户相关 getters
    isLoggedIn: state => state.user.isLoggedIn,
    userInfo: state => state.user.userInfo,
    userToken: state => state.user.token,
    userId: state => state.user.uid
  },
  modules: {
  }
})